import { useState, useRef, useEffect } from 'react';
import { fetchWithAuth } from '../lib/api';

interface SelectionInfo {
  paraId: string;
  text: string;
  areaId: string;
  chapterLabel: string;
}

interface ConversationTurn {
  role: 'user' | 'assistant';
  content: string;
}

interface StudioChatPanelProps {
  selection: SelectionInfo | null;
  lockMap: Record<string, boolean>;
  restUrl: string;
  onAccept: (paraId: string, newText: string) => void;
}

export function StudioChatPanel({
  selection,
  lockMap,
  restUrl,
  onAccept,
}: StudioChatPanelProps) {
  const [instruction, setInstruction] = useState('');
  const [proposal, setProposal] = useState<string | null>(null);
  const [conversationHistory, setConversationHistory] = useState<ConversationTurn[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [failedInstruction, setFailedInstruction] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const isLocked = selection ? (lockMap[selection.areaId] ?? false) : false;

  // Reset state when selection changes to a new paragraph
  useEffect(() => {
    setInstruction('');
    setProposal(null);
    setConversationHistory([]);
    setError(null);
    setFailedInstruction(null);
  }, [selection?.paraId]);

  // Focus input when selection arrives
  useEffect(() => {
    if (selection && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [selection?.paraId]);

  const handleRewrite = async (overrideInstruction?: string) => {
    const text = (overrideInstruction ?? instruction).trim();
    if (!text || !selection || loading) return;
    setLoading(true);
    setError(null);
    setFailedInstruction(null);

    try {
      const res = await fetchWithAuth(`${restUrl}edit/rewrite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          paraId: selection.paraId,
          selectedText: selection.text,
          instruction: text,
          conversationHistory,
          areaId: selection.areaId,
        }),
      });

      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const data = await res.json() as { rewrite: string };

      const newHistory: ConversationTurn[] = [
        ...conversationHistory,
        { role: 'user' as const, content: text },
        { role: 'assistant' as const, content: data.rewrite },
      ].slice(-20);

      setConversationHistory(newHistory);
      setProposal(data.rewrite);
      setInstruction('');
    } catch {
      setError('Could not get AI suggestion.');
      setFailedInstruction(text);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = () => {
    if (!proposal || !selection) return;
    onAccept(selection.paraId, proposal);
    setProposal(null);
    setInstruction('');
    setConversationHistory([]);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !loading) void handleRewrite();
  };

  return (
    <aside className="flex flex-col h-full" role="complementary" aria-label="AI Edit Assistant">
      {/* Header */}
      <div className="flex items-center px-4 py-3 border-b border-stone-200 flex-shrink-0">
        <p className="text-base font-semibold text-stone-900">Edit Passage</p>
        {selection?.chapterLabel && (
          <p className="text-xs text-stone-500 ml-2 truncate">{selection.chapterLabel}</p>
        )}
      </div>

      {/* Body */}
      {!selection ? (
        <div className="flex-1 flex items-center justify-center px-4 py-8">
          <p className="text-sm text-stone-400 italic text-center">
            Select a passage in your memoir to start editing with AI.
          </p>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {isLocked && (
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
              Chapter is locked — unlock to edit with AI.
            </div>
          )}

          {/* Original passage */}
          <div>
            <p className="text-sm font-semibold text-stone-600 mb-1">Original</p>
            <div className="rounded-md bg-stone-100 border border-stone-200 p-3">
              <p
                className="text-sm text-stone-800 leading-relaxed"
                style={{ fontFamily: "'Libre Baskerville', Georgia, serif" }}
              >
                {selection.text}
              </p>
            </div>
          </div>

          {/* Conversation history — user turns only */}
          {conversationHistory
            .filter(t => t.role === 'user')
            .map((turn, i) => (
              <div key={i} className="text-sm text-stone-500 italic">
                ↳ "{turn.content}"
              </div>
            ))}

          {/* Proposal */}
          {proposal && (
            <div>
              <p className="text-sm font-semibold text-blue-700 mb-1">Proposal</p>
              <div className="rounded-md bg-blue-50 border border-blue-200 p-3">
                <p
                  className="text-sm text-stone-800 leading-relaxed"
                  style={{ fontFamily: "'Libre Baskerville', Georgia, serif" }}
                >
                  {proposal}
                </p>
              </div>
            </div>
          )}

          {/* Pending indicator */}
          {loading && (
            <div className="flex items-center gap-2 text-sm text-stone-500" role="status">
              <svg className="animate-spin h-4 w-4 text-amber-500 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Rewriting…
            </div>
          )}

          {error && (
            <div
              role="alert"
              className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700 flex items-center justify-between gap-3"
            >
              <span>{error}</span>
              {failedInstruction && (
                <button
                  type="button"
                  onClick={() => void handleRewrite(failedInstruction)}
                  className="flex-none font-semibold underline hover:text-red-900 transition-colors"
                >
                  Retry
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Footer — only shown when there's a selection */}
      {selection && (
        <div className="border-t border-stone-200 p-4 space-y-3 flex-shrink-0">
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={instruction}
              onChange={e => setInstruction(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={proposal ? 'Refine further...' : 'Shorten this, make it more formal...'}
              disabled={loading || isLocked}
              className="flex-1 rounded-md border border-stone-300 px-3 py-2 text-sm
                focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent
                disabled:opacity-50 disabled:bg-stone-50"
            />
            <button
              type="button"
              onClick={() => void handleRewrite()}
              disabled={loading || !instruction.trim() || isLocked}
              aria-label="Rewrite with AI"
              className="px-3 py-2 rounded-md bg-amber-500 text-white text-sm font-semibold
                hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : '✎'}
            </button>
          </div>

          {proposal && (
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleAccept}
                className="flex-1 py-2 rounded-md bg-green-600 text-white text-sm font-semibold
                  hover:bg-green-700 transition-colors"
              >
                Accept
              </button>
              <button
                type="button"
                onClick={() => { setProposal(null); setConversationHistory([]); }}
                className="text-sm text-stone-500 hover:text-stone-700 transition-colors"
              >
                Reject
              </button>
            </div>
          )}
        </div>
      )}
    </aside>
  );
}
